import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { navigation } from '../data';
import { useCms } from '../context/CmsContext';

export default function Footer() {
  const { siteSettings } = useCms();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consent, setConsent] = useState({ analytics: false, marketing: false });

  // Hent eksisterende samtykke fra localStorage ved indlæsning
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ecolaser_cookie_consent');
      if (saved) {
        setConsent(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Kunne ikke hente samtykke fra localStorage:', e);
    }
  }, [showConsentModal]);

  const handleSave = (updatedConsent) => {
    setConsent(updatedConsent);
    if (window.updateConsent) {
      window.updateConsent(updatedConsent);
    }
    setShowConsentModal(false);
  };

  const handleAcceptAll = () => {
    const allGranted = { analytics: true, marketing: true };
    handleSave(allGranted);
  };

  const handleRejectAll = () => {
    const allDenied = { analytics: false, marketing: false };
    handleSave(allDenied);
  };

  return (
    <footer className="bg-zinc-50 border-t border-zinc-200 text-zinc-600 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Company Info */}
        <div className="flex flex-col gap-4">
          <Link to="/" className="inline-block">
            <img
              src={siteSettings.logo || "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png"}
              alt="Ecolaser Logo"
              className="h-14 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-4 mt-2">
            <a
              href={siteSettings.facebook_url || "https://www.facebook.com/share/18U7n9sHek/?mibextid=wwXIfr"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-brand-green hover:border-brand-green transition-colors"
              aria-label="Facebook"
            >
              <i className="fa-brands fa-facebook-f" />
            </a>
            <a
              href={siteSettings.linkedin_url || "https://www.linkedin.com/company/ecolaser-aps/"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-brand-green hover:border-brand-green transition-colors"
              aria-label="LinkedIn"
            >
              <i className="fa-brands fa-linkedin-in" />
            </a>
            <a
              href={siteSettings.instagram_url || "https://www.instagram.com/ecolaser_dk"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-brand-green hover:border-brand-green transition-colors"
              aria-label="Instagram"
            >
              <i className="fa-brands fa-instagram" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex flex-col gap-4">
          <h3 className="text-zinc-900 text-sm font-bold uppercase tracking-wider">Navigation</h3>
          <ul className="space-y-2.5 text-sm">
            {navigation.menu
              .filter((item) => item.href !== '/ydelser')
              .map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.href}
                    className="hover:text-brand-green transition-colors duration-200 flex items-center gap-2 text-zinc-600"
                  >
                    <i className="fa-solid fa-chevron-right text-[10px] text-brand-green/50" />
                    {item.label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>

        {/* Contact Details */}
        <div className="flex flex-col gap-4">
          <h3 className="text-zinc-900 text-sm font-bold uppercase tracking-wider">Kontakt</h3>
          <ul className="space-y-3 text-sm text-zinc-600">
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-location-dot text-brand-green mt-1 shrink-0" />
              <span>
                {siteSettings.company_name}<br />
                {siteSettings.address}
              </span>
            </li>
            <li className="flex items-center gap-3">
              <i className="fa-solid fa-phone text-brand-green shrink-0" />
              <a href={siteSettings.phoneTel} className="hover:text-brand-green transition-colors whitespace-nowrap">
                Tlf: {siteSettings.phone}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <i className="fa-solid fa-envelope text-brand-green shrink-0" />
              <a href={siteSettings.emailMailto} className="hover:text-brand-green transition-colors">
                {siteSettings.email}
              </a>
            </li>
          </ul>
        </div>

        {/* SMV Medlemskab */}
        <div className="flex flex-col gap-4">
          <h3 className="text-zinc-900 text-sm font-bold uppercase tracking-wider">SMVdanmark</h3>
          <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col gap-3">
            <img
              src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/smv-logo-rgb-300x82.jpg"
              alt="SMVdanmark Logo"
              className="h-10 w-auto object-contain self-start"
            />
            <p className="text-xs leading-relaxed text-zinc-500">
              Gennem vores medlemskab af SMVdanmark bakker vi op om det stærke, lokale erhvervsliv og sikrer os de bedste rammer til altid at levere topkvalitet for vores kunder.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-zinc-200 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-400">
        <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 flex-wrap justify-center md:justify-start">
          <span>&copy; {new Date().getFullYear()} {siteSettings.company_name}. Alle rettigheder forbeholdes. CVR-nr: {siteSettings.cvr}.</span>
          <span className="hidden md:inline text-zinc-300">|</span>
          <a
            href="https://www.iubenda.com/privacy-policy/68837785"
            className="iubenda-black iubenda-noiframe iubenda-embed hover:text-brand-green transition-colors font-medium"
            title="Privatlivspolitik "
          >
            Privatlivspolitik
          </a>
          <span className="hidden md:inline text-zinc-300">|</span>
          <a
            href="https://www.iubenda.com/privacy-policy/68837785/cookie-policy"
            className="iubenda-black iubenda-noiframe iubenda-embed hover:text-brand-green transition-colors font-medium"
            title="Cookiepolitik "
          >
            Cookiepolitik
          </a>
          <span className="hidden md:inline text-zinc-300">|</span>
          <button
            onClick={() => setShowConsentModal(true)}
            className="hover:text-brand-green underline cursor-pointer transition-colors font-medium"
          >
            Cookie-indstillinger
          </button>
        </div>
      </div>

      {/* Cookie Consent Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-zinc-100 flex flex-col gap-5 text-left animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-zinc-900 flex items-center gap-2">
                <i className="fa-solid fa-cookie-bite text-brand-green text-lg" />
                Cookie-indstillinger
              </h3>
              <button
                onClick={() => setShowConsentModal(false)}
                className="text-zinc-400 hover:text-zinc-600 transition-colors p-1"
                aria-label="Luk"
              >
                <i className="fa-solid fa-xmark text-sm" />
              </button>
            </div>

            <p className="text-zinc-500 text-xs leading-relaxed">
              Vi bruger cookies til at forbedre din oplevelse, analysere trafik og tilpasse vores markedsføring. Du kan til enhver tid ændre eller tilbagekalde dit samtykke herunder.
            </p>

            <div className="flex flex-col gap-3">
              {/* Nødvendige cookies */}
              <div className="flex items-start justify-between gap-4 p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold text-zinc-800">Nødvendige cookies</span>
                  <span className="text-[10px] leading-normal text-zinc-500">Sikrer basale funktioner og sikkerhed på hjemmesiden. Kan ikke fravælges.</span>
                </div>
                <span className="text-brand-green text-[10px] font-bold uppercase mt-1 shrink-0">Altid aktiv</span>
              </div>

              {/* Statistik cookies */}
              <div className="flex items-start justify-between gap-4 p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold text-zinc-800">Statistik & Analyse</span>
                  <span className="text-[10px] leading-normal text-zinc-500">Hjælper os med at forstå, hvordan besøgende bruger hjemmesiden (Google Analytics).</span>
                </div>
                <button
                  onClick={() => setConsent(prev => ({ ...prev, analytics: !prev.analytics }))}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none mt-1 ${consent.analytics ? 'bg-brand-green' : 'bg-zinc-300'}`}
                  aria-label="Tillad statistik cookies"
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${consent.analytics ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Marketing cookies */}
              <div className="flex items-start justify-between gap-4 p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-bold text-zinc-800">Marketing & Annoncering</span>
                  <span className="text-[10px] leading-normal text-zinc-500">Anvendes til at vise målrettede annoncer og måle effekten af vores kampagner (Google Ads & Meta Pixel).</span>
                </div>
                <button
                  onClick={() => setConsent(prev => ({ ...prev, marketing: !prev.marketing }))}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none mt-1 ${consent.marketing ? 'bg-brand-green' : 'bg-zinc-300'}`}
                  aria-label="Tillad marketing cookies"
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${consent.marketing ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                onClick={handleRejectAll}
                className="flex-1 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg text-xs font-bold transition-colors cursor-pointer text-center"
              >
                Afvis alle
              </button>
              <button
                onClick={() => handleSave(consent)}
                className="flex-1 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer text-center"
              >
                Gem indstillinger
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-3 py-2 bg-brand-green hover:bg-brand-green/90 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer text-center"
              >
                Accepter alle
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
